import { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export function VoiceDictation({ onText, testId = "voice-dictate" }) {
  const [state, setState] = useState("idle");
  const [secs, setSecs] = useState(0);
  const rec = useRef(null);
  const timer = useRef(null);

  useEffect(() => () => { clearInterval(timer.current); rec.current?.stream?.getTracks().forEach((t) => t.stop()); }, []);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setState("transcribing");
        const type = mr.mimeType || "audio/webm";
        const ext = type.includes("mp4") ? "mp4" : "webm";
        const fd = new FormData();
        fd.append("file", new Blob(chunks, { type }), `dictation.${ext}`);
        try {
          const { data } = await api.post("/transcribe", fd);
          if (data.text?.trim()) { onText(data.text.trim()); toast.success("Dictation added"); }
          else toast.info("No speech detected");
        } catch (e) { toast.error(errMsg(e)); }
        setState("idle");
      };
      mr.start();
      rec.current = mr;
      setSecs(0);
      timer.current = setInterval(() => setSecs((s) => s + 1), 1000);
      setState("recording");
    } catch (e) {
      toast.error("Microphone access was blocked. Allow mic access in your browser to dictate.");
    }
  };

  const stop = () => { clearInterval(timer.current); rec.current?.stop(); };

  if (state === "recording")
    return (
      <div className="flex items-center gap-3 rounded-full border border-red-200 bg-red-50 pl-4 pr-1 py-1" data-testid={`${testId}-recording`}>
        <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
        <div className="flex items-center gap-[3px] h-5">
          {[0, 1, 2, 3, 4, 5].map((i) => <span key={i} className="wave-bar" style={{ animationDelay: `${i * 0.12}s` }} />)}
        </div>
        <span className="font-mono text-xs text-red-700">{fmt(secs)}</span>
        <Button size="sm" variant="destructive" className="rounded-full h-8" onClick={stop} data-testid={`${testId}-stop-button`}>
          <Square className="h-3 w-3 mr-1.5" /> Stop
        </Button>
      </div>
    );

  return (
    <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={start} disabled={state === "transcribing"} data-testid={`${testId}-start-button`}>
      {state === "transcribing" ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Transcribing…</> : <><Mic className="h-4 w-4 mr-2 text-primary" />Dictate</>}
    </Button>
  );
}
